import { useState } from "react";
import { Headphones, LockKeyhole, LogOut, Mic, UserRound } from "lucide-react";
import AccountInfoModal from "../AccountInfoModal";
import NameAndVoiceModal from "../NameAndVoiceModal";
import ChangePasswordModal from "../ChangePasswordModal";
import MicSilenceTestModal from "../modal/MicSilenceTestModal";
import { useMicPermissionStore } from "@/features/tutor/store/useMicPermissionStore";
import LessonDrawer from "./LessonDrawer";

export type SettingsSection = "account" | "voice" | "audio" | "password";
const sections = [
  { id: "account", label: "Account information", icon: UserRound },
  { id: "voice", label: "Name & voice", icon: Headphones },
  { id: "audio", label: "Speaker & microphone", icon: Mic },
  { id: "password", label: "Set up a password", icon: LockKeyhole },
] as const;

/** Presentation only: each panel retains its production services and validation. */
export default function SettingsSheet({
  initialSection,
  name,
  onClose,
  onLogout,
  supportSession = false,
}: {
  initialSection: SettingsSection;
  name: string;
  onClose: () => void;
  onLogout: () => void;
  supportSession?: boolean;
}) {
  const [selected, setSection] = useState(initialSection);
  // Support sessions keep only local device settings: the other panels and
  // Sign out act on the target's durable account. The support banner's
  // "End support session" is the exit, so hiding Sign out traps no one.
  const section: SettingsSection = supportSession ? "audio" : selected;
  const reinitForDeviceChange = useMicPermissionStore(
    (s) => s.reinitForDeviceChange,
  );
  return (
    <LessonDrawer
      title="Profile and settings"
      eyebrow="Profile"
      closeLabel="Close profile and settings"
      className="et-settings-sheet"
      onClose={onClose}
    >
      <div className="et-settings-heading">
        <p>{name}</p>
      </div>
      <div className="et-settings-layout">
        <nav aria-label="Settings sections">
          {sections
            .filter(({ id }) => !supportSession || id === "audio")
            .map(({ id, label, icon: Icon }) => (
              <button
                type="button"
                key={id}
                aria-current={section === id ? "page" : undefined}
                onClick={() => setSection(id)}
              >
                <Icon size={18} />
                {label}
              </button>
            ))}
          {supportSession ? null : (
            <button type="button" onClick={onLogout}>
              <LogOut size={18} />
              Sign out
            </button>
          )}
        </nav>
        <div className="et-settings-content">
          {section === "account" && (
            <AccountInfoModal
              isOpen
              onClose={onClose}
              onManageVoice={() => setSection("voice")}
            />
          )}
          {section === "voice" && (
            <NameAndVoiceModal isOpen onClose={onClose} />
          )}
          {section === "audio" && (
            <MicSilenceTestModal
              isOpen
              embedded
              onClose={onClose}
              onDeviceConfirmed={reinitForDeviceChange ?? undefined}
            />
          )}
          {section === "password" && (
            <ChangePasswordModal isOpen onClose={onClose} />
          )}
        </div>
      </div>
    </LessonDrawer>
  );
}
